import React, { useState, useEffect } from 'react';
import { Control, Errors, actions } from 'react-redux-form';
import { get } from 'lodash';
import { Page, FormGroup, required, SubmitButton, PendingButton } from 'components/common';
import { useClient, useUserContext } from '../../hooks';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useForm } from 'hooks';
import { Button, Alert } from 'reactstrap';
import { NotificationManager } from 'react-notifications';

const ErrorAlert = ({ children }) => <Alert color="danger">{children}</Alert>;

const LOADING = 'LOADING';
const ERROR = 'ERROR';
const ACCEPT = 'ACCEPT';
const SUCCESS = 'SUCCESS';

const errors = {
  InviteNotFound: '',
  InviteExpired: '',
  EmployeeAlreadyLinked: '',
  InviteAlreadyAccepted: '',
};

const InviteAuth = ({ match, t }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    token: '',
  });
  const { token } = match.params;
  const [state, setState] = useState(LOADING);
  const [invite, setInvite] = useState();
  const [error, setError] = useState();
  const [pending, setPending] = useState(false);
  const history = useHistory();
  const { Form: SetPasswordForm, dispatch: setPasswordDispatch } = useForm({
    enableStoreLogging: true,
  });
  const { query } = useUserContext();
  const client = useClient();

  const reset = () => {
    setState(null);
    setInvite(null);
    setError(null);
    setPending(false);
  };

  const setInviteError = (ex) => {
    console.error('Error during API call:', ex);

    const code = get(ex, 'response.data.code');
    const errorMessage =
      errors[code] ||
      get(ex, 'response.data.detail') ||
      'An error occurred. Please Re-check the link and try again.';

    if (!errorMessage) {
      console.error('Unable to determine error message. Full response:', ex.response);
    }

    setState(ERROR);
    setError(t(errorMessage));
  };

  const handlePasswordReset = async (data) => {
    setPasswordDispatch(actions.resetErrors('errors'));

    try {
      await client.post('authorization/reset', { ...data, token });
      await onLogin(data);
    } catch (ex) {
      const responseData = get(ex, 'response.data') || {};

      if (responseData) {
        setPasswordDispatch(
          actions.setErrors(
            'errors.email',
            responseData.email ? 'A user with this email address already exists' : false
          )
        );
        setPasswordDispatch(actions.setErrors('errors.password', responseData.password));
      }
    } finally {
      setPasswordDispatch(actions.setPending('local', false));
    }
  };

  const onLogin = async ({ email, password }) => {
    try {
      await client.post('/auth', {
        email: email.toLowerCase(),
        password,
      });
      await query.refetch({ throwOnError: true });
      setState(ACCEPT);
    } catch (ex) {
      handleLoginError(ex);
    } finally {
      setPending(false);
      setPasswordDispatch(actions.setPending('local', false));
    }
  };

  const handleLoginError = (ex) => {
    const status = get(ex, 'response.status');

    if (status === 400) {
      NotificationManager.error(t('Invalid username or password'));
    } else {
      NotificationManager.error(t('Unable to log in at this time'));
    }
  };

  const onAcceptInvite = async () => {
    setPending(true);
    setError(null);

    try {
      await client.post('authorization/invite/accept', { token });
      
      setTimeout(() => {
        window.location = '/home'
      }, 1000);
    } catch (ex) {
      setInviteError(ex);
    } finally {
      setPending(false);
    }
  };

  const onSetPassword = async (values) => {
    const { password } = values;

    const { company_name, email, first_name, last_name, token } = formData;

    const postData = { company_name, email, first_name, last_name, token, password };

    setPasswordDispatch(actions.resetErrors('errors'));

    try {
      await client.post('authorization/invite/reset', postData);
      await onLogin(postData);
      history.push('/authorization/success');
    } catch (ex) {
      const responseData = get(ex, 'response.data') || {};

      Object.keys(responseData).forEach((key) => {
        const errorMessage = responseData[key];
        if (errorMessage) {
          NotificationManager.error(errorMessage);
          setPasswordDispatch(actions.setErrors(`errors.${key}`, errorMessage));
        }
      });
    } finally {
      setPasswordDispatch(actions.setPending('local', false));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      reset();

      try {
        setState(LOADING);

        const user = await client.get('me').catch((err) => null);

        if (user) {
          setInvite(user);
          setFormData({
            ...user,
            password: '',
          });
          setState(ACCEPT);
          return;
        }

        const response = await client.post('authorization/invite/check', { token });

        if (!response || !response.data || !response.data.email) {
          console.error('API response or email not available. Full response:', response);
          setInviteError(new Error('API response or email not available.'));
          return;
        }

        setInvite(response.data);

        setFormData({
          ...response.data,
          password: '',
        });

        setState(ACCEPT);
      } catch (ex) {
        setInviteError(ex);
      }
    };

    fetchData();
  }, [token]);

  if (state === LOADING) {
    return (
      <Page className="text-center mt-3">
          <div className="row justify-content-center">
            <div className="col-md-6">
                <h2><span className="fa fa-spinner fa-spin mt-2" />{" "}<Trans>Loading... Please Wait</Trans></h2>
            </div>
          </div>
      </Page>
    );
  }

  if (state !== ACCEPT) {
    return (
      <Page className="text-center mt-3">
        <div className="">
          <div className="row justify-content-center">
            <div className="col-md-6">
              {state === ERROR && error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page title="" className="text-center mt-3">
      <div className="">
      {invite && (<Trans><h1>Welcome, {invite.first_name} {invite.last_name}!</h1></Trans>)}
        <div className="row justify-content-center">
          <div className="col-md-6">
            {invite && (
              <Trans>
                <p>
                  You've been invited by <strong>{invite.company_name}</strong> to join Hesaab.
                </p>
                <p>
                  We are thrilled to welcome you aboard and be a part of {invite.company_name}'s
                  team. Get ready for an exciting journey with Hesaab. We hope you find the
                  experience nice and comfortable!
                </p>
              </Trans>
            )}

            { invite && !invite.is_active_user && (<SetPasswordForm
              onSubmit={onSetPassword}
              validators={{
                password: { required },
                confirm_password: { required },
                '': {
                  passwordsMatch: (v) => v.password === v.confirm_password,
                },
              }}
            >
              <div>
                {state === ERROR && error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
                <hr />
                <h5 className="mb-3">
                  <Trans>User Information</Trans>
                </h5>
                <div className="d-flex justify-content-center text-left">
                  <div>
                    <FormGroup label="Full Name" model=".full_name">
                      <Control.text
                        model=".full_name"
                        value={`${formData.first_name} ${formData.last_name}`}
                        disabled
                      />
                    </FormGroup>
                    <FormGroup label="Email" model=".email" style={{ width: '20rem' }}>
                      <Control.text model=".email" value={formData.email} disabled />
                    </FormGroup>
                  </div>
                </div>
              </div>
              <div>
                <hr />
                <h5 className="mb-3">
                  <Trans>Set your password</Trans>
                </h5>
                <div className="d-flex justify-content-center text-left">
                  <div>
                    <FormGroup label="Password" model=".password" style={{ width: '20rem' }}>
                      <Control.text type="password" model=".password" />
                    </FormGroup>
                    <FormGroup label="Confirm Password" model=".confirm_password">
                      <Control.text type="password" model=".confirm_password" />
                    </FormGroup>
                    <Errors model="errors.password" component={ErrorAlert} />
                    <div className="d-flex justify-content-between">
                      <SubmitButton
                        style={{ width: '100%' }}
                        type="submit"
                        className="btn btn-primary"
                      >
                        Set Password
                      </SubmitButton>
                    </div>
                  </div>
                </div>
                <hr />
                <div className="text-center mt-2">
                  <Trans>Passwords must be at least 8 characters long, include both letters and numbers,
                  contain at least one uppercase letter, and include at least one special character.</Trans>
                </div>
              </div>
            </SetPasswordForm>)}

            {invite && invite.is_active_user && (
              <PendingButton
                color="primary"
                onClick={onAcceptInvite}
                pending={pending}
              >
                Accept Invite
              </PendingButton>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};


export default InviteAuth;
